/**
 * Nieuwe versie na een deploy meteen gebruiken (Sanju 29/10: "ik zie de oude versie nog").
 *
 * De service worker activeert zich al direct (skipWaiting + clientsClaim in vite.config.ts), maar
 * een open pagina blijft de oude JS draaien. Bij een nieuwe controller: melding met "Vernieuwen",
 * en anders volledig herladen bij de volgende navigatie naar een andere pagina in de app — een
 * veilig moment. Nooit herladen op een tabblad-wissel: op de iPad gaat de app ook naar de
 * achtergrond als de plaatser de camera opent, en dan zou een half ingevuld formulier verloren gaan.
 * Open PWA's (plaatsers houden de app de hele dag open) zoeken elke 30 min en bij terugkeer naar
 * het tabblad naar een update.
 */
import i18n from 'i18next';

export const UPDATE_INTERVAL_MS = 30 * 60_000;
const MIN_TUSSEN_CHECKS_MS = 5 * 60_000;

/** Eerste bezoek: clientsClaim neemt de pagina ook over, maar dan draait er geen oude versie. */
export const isUpdate = (hadController: boolean) => hadController;

/** Enkel bij een echte paginawissel herladen, niet bij een andere filter/weergave (?weergave=, #). */
export function isAnderePagina(van: string, naar: string): boolean {
  const pad = (u: string) => decodeURI(new URL(u, 'https://x').pathname).replace(/\/+$/, '') || '/';
  try {
    return pad(van) !== pad(naar);
  } catch { return false; }
}

function toonMelding() {
  if (document.getElementById('cbridge-nieuwe-versie')) return;
  const balk = document.createElement('div');
  balk.id = 'cbridge-nieuwe-versie';
  balk.setAttribute('role', 'status');
  balk.className = 'fixed top-3 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 rounded-xl bg-gray-900 text-white text-sm px-4 py-2 shadow-lg';
  const tekst = document.createElement('span');
  tekst.textContent = i18n.t('pwa.nieuweVersie');
  const knop = document.createElement('button');
  knop.type = 'button';
  knop.className = 'rounded-lg bg-gold px-3 py-1 text-xs font-semibold text-white hover:opacity-90';
  knop.textContent = i18n.t('pwa.vernieuwen');
  knop.addEventListener('click', () => window.location.reload());
  balk.append(tekst, knop);
  document.body.appendChild(balk);
}

/** Na een update: de volgende navigatie (link of terug/vooruit) naar een andere pagina wordt een volledige paginalading. */
function herlaadBijVolgendeNavigatie() {
  let huidig = window.location.href;
  window.addEventListener('popstate', () => {
    if (isAnderePagina(huidig, window.location.href)) window.location.reload();
    huidig = window.location.href;
  });
  const origineel = history.pushState.bind(history);
  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    const van = window.location.href;
    origineel(data, unused, url);
    huidig = window.location.href;
    if (url != null && isAnderePagina(van, String(url))) window.location.reload();
  };
}

export function startServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  let klaargezet = false;

  sw.addEventListener('controllerchange', () => {
    if (!isUpdate(hadController) || klaargezet) return;
    klaargezet = true;
    toonMelding();
    herlaadBijVolgendeNavigatie();
  });

  let laatsteCheck = Date.now();
  const zoekUpdate = (reg: ServiceWorkerRegistration) => {
    laatsteCheck = Date.now();
    reg.update().catch(() => { /* offline: volgende keer */ });
  };

  window.addEventListener('load', () => {
    sw.register('/sw.js', { scope: '/' }).then(reg => {
      setInterval(() => zoekUpdate(reg), UPDATE_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && Date.now() - laatsteCheck > MIN_TUSSEN_CHECKS_MS) zoekUpdate(reg);
      });
    }).catch(() => { /* registratie mislukt — niet kritiek */ });
  });
}
